import { loadScript, customerSafeError } from '../loadScript.js';

/**
 * HC-4: the card is tokenized in the customer's browser by the provider's own
 * script and sent directly to them. The card number never reaches our servers.
 *
 * Square's Web Payments SDK renders its own card fields into an element we
 * supply, so the fields are Square's iframe rather than our inputs — which is
 * why this tokenizer also owns mounting and unmounting them.
 */
let cardInstance = null;

/** Mounts Square's card fields into `container`. Returns a teardown function. */
export async function mount({ config, container }) {
  const Square = await loadScript(config.scriptUrl, { globalName: 'Square' });

  const payments = Square.payments(config.applicationId, config.locationId);
  const card = await payments.card({
    style: {
      // Matched to our own input styling so Square's frame does not look pasted in.
      input: { fontSize: '15px', color: '#181816' },
      '.input-container': { borderColor: '#e2e2db', borderRadius: '8px' },
      '.input-container.is-focus': { borderColor: '#386db2' },
      '.input-container.is-error': { borderColor: '#ef4444' },
    },
  });

  await card.attach(container);
  cardInstance = card;

  return async () => {
    try {
      await card.destroy();
    } catch {
      /* already gone */
    }
    cardInstance = null;
  };
}

/**
 * @returns {Promise<{sourceId: string, verificationToken?: string}>}
 * @throws  {Error} with a customer-readable message (HC-9)
 */
export async function tokenize() {
  if (!cardInstance) {
    throw new Error('The card form is not ready yet. Please wait a moment and try again.');
  }

  const result = await cardInstance.tokenize();

  if (result.status === 'OK') return { sourceId: result.token };

  const first = result.errors?.[0] ?? {};
  const field = String(first.field ?? '');

  // HC-9: instructions the customer can act on, never Square's own wording.
  const byField = {
    cardNumber: 'Please check the card number and try again.',
    expirationDate: 'Please check the expiry date and try again.',
    cvv: 'Please check the security code on your card.',
    postalCode: 'Please check the postcode for this card.',
  };

  if (byField[field]) throw new Error(byField[field]);
  throw customerSafeError(first.type ?? result.status, first.message);
}

export default { mount, tokenize, rendersOwnFields: true };
